/**
 * EWSIndicatorCustomersDrawer - Drawer showing customers filtered by indicator level
 *
 * Displays a paginated table of customers who have a specific risk indicator level.
 * Opened when clicking a bar in the analytics section.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DrawerProvider,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Loader,
  Table,
  type TableColumn,
} from 'equal-ds-ui';
import { X, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  EWSIndicatorCustomersDrawerProps,
  EWSCustomer,
  PaginationInfo,
  formatINR,
  formatIndianNumber,
} from '../../../types/ews.types';
import { ewsApiService } from '../../../services/ews-api.service';
import { Toast } from '../../ui/Toast';

/**
 * Get risk level badge color classes.
 */
function getRiskLevelColor(level: string): string {
  switch (level) {
    case 'CRITICAL':
      return 'bg-red-100 text-red-800';
    case 'HIGH':
      return 'bg-orange-100 text-orange-800';
    case 'MEDIUM':
      return 'bg-yellow-100 text-yellow-800';
    case 'LOW':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Loading state for table using Equal DS Loader.
 */
const TableLoading: React.FC = () => (
  <div className="flex items-center justify-center py-16">
    <Loader size={72} cellSize={20} />
  </div>
);

/**
 * Empty state when no customers match the filter.
 */
const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
    <svg
      className="w-12 h-12 mb-3 text-gray-300"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
    <p className="text-sm">No customers found</p>
    <p className="text-xs text-gray-400 mt-1">No customers match this filter criteria</p>
  </div>
);

export const EWSIndicatorCustomersDrawer: React.FC<EWSIndicatorCustomersDrawerProps> = ({
  isOpen,
  onClose,
  indicator,
  indicatorName,
  level,
  levelLabel,
  totalCount,
  batchId,
  onCustomerClick,
}) => {
  const [customers, setCustomers] = useState<EWSCustomer[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const pageSize = 15; // Show more rows to fill the drawer

  /**
   * Load customers for the current page.
   */
  const loadCustomers = useCallback(async () => {
    if (!isOpen || !indicator || !level) return;

    setIsLoading(true);
    try {
      const response = await ewsApiService.getCustomers({
        page: currentPage,
        pageSize,
        batchId,
        indicator,
        indicatorLevel: level,
      });
      setCustomers(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('[EWS] Failed to load indicator customers:', error);
      setCustomers([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, indicator, level, batchId, currentPage, pageSize]);

  // Track previous indicator/level to detect actual changes
  const prevIndicatorRef = useRef<string | null>(null);
  const prevLevelRef = useRef<string | null>(null);

  // Load customers when drawer opens or page changes
  useEffect(() => {
    if (isOpen) {
      loadCustomers();
    }
  }, [isOpen, loadCustomers]);

  // Reset page only when indicator/level actually changes (not on back navigation)
  useEffect(() => {
    if (isOpen) {
      const indicatorChanged = prevIndicatorRef.current !== indicator;
      const levelChanged = prevLevelRef.current !== level;

      if (indicatorChanged || levelChanged) {
        setCurrentPage(1);
      }

      // Update refs after comparison
      prevIndicatorRef.current = indicator;
      prevLevelRef.current = level;
    }
  }, [isOpen, indicator, level]);

  /**
   * Handle CSV download.
   */
  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Fetch all pages for download (up to 1000 records)
      const allCustomers: EWSCustomer[] = [];
      let page = 1;
      const maxPages = Math.ceil(Math.min(totalCount, 1000) / 100);

      while (page <= maxPages) {
        const response = await ewsApiService.getCustomers({
          page,
          pageSize: 100,
          batchId,
          indicator,
          indicatorLevel: level,
        });
        allCustomers.push(...response.data);
        page++;
      }

      // Generate CSV
      const headers = ['Consent ID', 'Current Balance', 'Risk Level', 'Risk Score', 'FOIR', 'Active EMIs', 'Active SIPs'];
      const rows = allCustomers.map(c => [
        c.consentId || c.trackingId || '',
        c.currentBalance?.toString() || '0',
        c.riskLevel || '',
        c.riskScore?.toString() || '0',
        c.foir?.toString() || '',
        c.activeEMIsCount?.toString() || '0',
        c.activeSIPs?.toString() || '0',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
      ].join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${indicator}_${level}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[EWS] Failed to download CSV:', error);
      setDownloadError('Failed to download CSV. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Table columns configuration.
   */
  const columns: TableColumn<EWSCustomer>[] = [
    {
      key: 'consentId',
      header: 'Consent ID',
      render: (row) => (
        <span className="font-mono text-sm text-gray-700 truncate max-w-[150px] block" title={row.consentId || row.trackingId}>
          {(row.consentId || row.trackingId || '').slice(0, 13)}...
        </span>
      ),
    },
    {
      key: 'currentBalance',
      header: 'Balance',
      render: (row) => (
        <span className="text-sm font-medium">{formatINR(row.currentBalance || 0)}</span>
      ),
    },
    {
      key: 'riskLevel',
      header: 'Risk Level',
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getRiskLevelColor(row.riskLevel || 'LOW')}`}>
          {row.riskLevel || 'LOW'}
        </span>
      ),
    },
    {
      key: 'riskScore',
      header: 'Score',
      render: (row) => (
        <span className="text-sm">{row.riskScore || 0}/40</span>
      ),
    },
    {
      key: 'foir',
      header: 'FOIR',
      render: (row) => (
        <span className="text-sm">{row.foir ? `${row.foir.toFixed(1)}%` : '-'}</span>
      ),
    },
    {
      key: 'activeEMIsCount',
      header: 'EMIs',
      render: (row) => (
        <span className="text-sm">{row.activeEMIsCount || 0}</span>
      ),
    },
    {
      key: 'activeSIPs',
      header: 'SIPs',
      render: (row) => (
        <span className="text-sm">{row.activeSIPs || 0}</span>
      ),
    },
  ];

  const totalPages = pagination?.totalPages || 1;

  // Early return if not open
  if (!isOpen) {
    return null;
  }

  return (
    <DrawerProvider side="right" open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerOverlay />
      <Drawer
        variant="overlay"
        width="50%"
        className="border-border-default bg-white"
      >
        <DrawerContent className="h-full flex flex-col">
          {/* Header */}
          <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {indicatorName} - {levelLabel}
                </h2>
                <p className="text-sm text-gray-700 mt-0.5">
                  {formatIndianNumber(totalCount)} customers
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDownload}
                  disabled={isDownloading || totalCount === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className={`w-4 h-4 ${isDownloading ? 'animate-bounce' : ''}`} />
                  {isDownloading ? 'Downloading...' : 'Download'}
                </button>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto px-6 py-4">
            {isLoading ? (
              <TableLoading />
            ) : customers.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="equal-ds-root">
                <Table
                  data={customers}
                  columns={columns}
                  onRowClick={onCustomerClick}
                  className="cursor-pointer"
                />
              </div>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && pagination && pagination.totalPages > 1 && (
            <div className="flex-shrink-0 pl-6 pr-20 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">
                  Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, pagination.totalRecords)} of {formatIndianNumber(pagination.totalRecords)}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-3 py-1.5 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-lg min-w-[80px] text-center">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>

      {/* Error Toast */}
      {downloadError && (
        <Toast
          message={downloadError}
          variant="error"
          onClose={() => setDownloadError(null)}
        />
      )}
    </DrawerProvider>
  );
};

export default EWSIndicatorCustomersDrawer;
